import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useState } from "react";
import {
    useDeleteSendingEmail,
    useUpdateSendingEmail,
} from "@api/sendingEmail.api";
import type { SendingEmailWithDomain } from "@api/sendingEmail.api";
import { useWorkspaceSendingOwner } from "@hooks/useWorkspaceSendingOwner";
import { useConnectGmail } from "../useConnectGmail";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { EditSendingEmailModal } from "../EditSendingEmailModal";
import { SuggestedConnectButton } from "../SuggestedConnectButton";
import {
    isSuggestedSendingEmailRow,
    type SendingEmailRow,
} from "../sendingEmailsViewConfig";

const GmailSuggestedSenderActions = memo(() => {
    const { connect, isConnecting } = useConnectGmail();
    return <SuggestedConnectButton loading={isConnecting} onClick={connect} />;
});

const ConnectedSenderActions = memo(
    ({ email }: { email: SendingEmailWithDomain }) => {
        const [showDeleteConfirmation, setShowDeleteConfirmation] =
            useState(false);
        const [showEditModal, setShowEditModal] = useState(false);
        const owner = useWorkspaceSendingOwner();
        const updateEmail = useUpdateSendingEmail(owner);
        const deleteEmail = useDeleteSendingEmail(owner);
        const { toast } = useToaster();

        const handleSetDefault = useCallback(async () => {
            try {
                await updateEmail.mutateAsync({
                    emailId: email.id,
                    body: { isDefault: true },
                });
                toast("Default sender updated");
            } catch {
                // The API client already toasts the error body.
            }
        }, [email.id, toast, updateEmail]);

        const confirmDelete = useCallback(async () => {
            try {
                await deleteEmail.mutateAsync(email.id);
                setShowDeleteConfirmation(false);
                toast("Sender email removed");
            } catch {
                // The API client already toasts the error body.
            }
        }, [deleteEmail, email.id, toast]);

        return (
            <ViewTableCell captureClicks>
                <Menu.More>
                    <Menu.Item onClick={() => setShowEditModal(true)}>
                        Edit
                    </Menu.Item>
                    {!email.isDefault && (
                        <Menu.Item
                            onClick={() => {
                                void handleSetDefault();
                            }}
                        >
                            Set as default
                        </Menu.Item>
                    )}
                    <Menu.Item
                        destructive
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Remove email
                    </Menu.Item>
                </Menu.More>

                <EditSendingEmailModal
                    show={showEditModal}
                    email={email}
                    onClose={() => setShowEditModal(false)}
                />

                <ConfirmationModal
                    show={showDeleteConfirmation}
                    onHide={() => setShowDeleteConfirmation(false)}
                    closeButton
                    title="Remove sender email?"
                    question={`Are you sure you want to remove "${email.email}"?`}
                    onConfirm={() => {
                        void confirmDelete();
                    }}
                    onReject={() => setShowDeleteConfirmation(false)}
                    yes="Remove"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />
            </ViewTableCell>
        );
    },
);

export const SendingEmailActionsCell = memo(
    ({ row }: CellContext<ViewTableFeatures, SendingEmailRow, string>) => {
        const email = row.original;
        if (isSuggestedSendingEmailRow(email)) {
            return <GmailSuggestedSenderActions />;
        }
        return <ConnectedSenderActions email={email} />;
    },
);
